import React from "react";
import { reportRenderError } from "@/lib/errorReporter";

/**
 * Catches a render-time throw and shows a fallback instead of unmounting the tree.
 *
 * Without one, React 16+ unmounts the *entire* application when any component
 * throws during render — the operator gets a blank white page with nothing to
 * click and no indication of what happened. That is the whole reason this exists.
 *
 * **What a boundary does not catch**, so its coverage is not overstated:
 * throws inside event handlers, rejected promises and other asynchronous work,
 * errors raised in `setTimeout`/`requestAnimationFrame` callbacks, and errors
 * thrown by the boundary's own fallback. React only routes render, lifecycle and
 * constructor errors here. An event handler that throws still leaves the screen
 * intact, so it is a different failure with a different fix — try/catch at the
 * handler.
 *
 * Must be a class: `getDerivedStateFromError` and `componentDidCatch` have no
 * hook equivalent, which is the one remaining thing hooks cannot express.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { failed: false };
    this.reset = this.reset.bind(this);
  }

  static getDerivedStateFromError() {
    // No part of the error is kept in state. Rendering an exception message or a
    // stack trace leaks internal paths and query shapes to whoever is looking at
    // the screen, and it is never actionable to the person reading it.
    return { failed: true };
  }

  componentDidCatch(error, info) {
    // The console is still written to — it is what a developer has open — but it
    // is no longer the only reporter, and outside development it is not a
    // private channel either. `lib/posthog.js` initialises with
    // `enable_recording_console_log: true`, so whatever is logged here is
    // shipped to PostHog alongside the session replay and retained there; its
    // `SENSITIVE` regex is a `maskInputFn` and masks input *elements* only,
    // never console arguments.
    //
    // An exception message interpolates the value that broke, and on these
    // screens that is a borrower, an account number or a loan reference —
    // which is exactly why `errorReporter.js` builds its payload field by named
    // field and excludes the message on every branch. Logging the whole `Error`
    // in production would hand a third party the one string that reporter
    // exists to withhold. The class name survives, because it is what makes a
    // replay triageable and it carries nothing of the record.
    const label = `[${this.props.area || "app"}] render failed`;
    /* eslint-disable no-console */
    if (process.env.NODE_ENV === "production") {
      console.error(label, error?.name || "Error");
    } else {
      console.error(label, error, info);
    }
    /* eslint-enable no-console */

    // `onError` remains the seam, and it now DEFAULTS to the real reporter
    // rather than being wired at each call site. A boundary added later
    // therefore reports without anyone remembering to pass a prop — the failure
    // mode of per-site wiring is a new boundary that silently reports nothing,
    // which looks identical to no errors happening.
    //
    // React 19 routes a caught error to its own `onCaughtError` and does NOT
    // rethrow it to `window.onerror`, so nothing else in the page observes
    // these. If this does not report them, they are reported nowhere.
    //
    // Passing `onError` explicitly overrides the default, which is how the tests
    // observe it without sending anything. The reporter is inert unless
    // REACT_APP_ERROR_REPORTER_DSN was set at build time.
    const report =
      typeof this.props.onError === "function"
        ? this.props.onError
        : reportRenderError;
    try {
      report(error, info, this.props.area);
    } catch {
      // A reporter that throws must not replace the error it was reporting.
    }
  }

  componentDidUpdate(prevProps) {
    // Recover on navigation. Without this the fallback persists after the
    // operator moves to a working screen, which reads as the whole app being
    // broken rather than one page.
    if (!this.state.failed) return;
    if (!keysChanged(prevProps.resetKeys, this.props.resetKeys)) return;
    this.reset();
  }

  reset() {
    this.setState({ failed: false });
  }

  render() {
    if (!this.state.failed) return this.props.children;
    if (this.props.fallback) return this.props.fallback(this.reset);
    return <DefaultFallback area={this.props.area} onRetry={this.reset} />;
  }
}

/** Whether two `resetKeys` arrays differ, treating a missing array as no keys. */
function keysChanged(before, after) {
  const a = before || [];
  const b = after || [];
  if (a.length !== b.length) return true;
  return a.some((value, i) => !Object.is(value, b[i]));
}

/**
 * The fallback shown when no custom one is supplied.
 *
 * Names the area that failed rather than the exception, so the operator can say
 * what broke without reading a stack trace, and offers both a retry (re-mounts
 * the subtree, enough for a transient failure) and a reload (for a corrupted
 * client state that a re-render will not clear).
 */
function DefaultFallback({ area, onRetry }) {
  return (
    <div
      role="alert"
      className="m-6 rounded-lg border border-red-200 bg-red-50 p-6 text-left"
    >
      <h2 className="text-lg font-semibold text-red-900">
        {area ? `${area} could not be displayed` : "This screen could not be displayed"}
      </h2>
      <p className="mt-2 max-w-prose text-sm text-red-800">
        Something went wrong while drawing this page. Any details you had not
        saved may not have been kept. The rest of the portal is still working, so
        you can carry on elsewhere.
      </p>
      <div className="mt-4 flex gap-3">
        <button
          type="button"
          onClick={onRetry}
          className="rounded-md bg-red-600 px-3 py-2 text-sm font-medium text-white hover:bg-red-700"
        >
          Try again
        </button>
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="rounded-md border border-red-300 bg-white px-3 py-2 text-sm font-medium text-red-800 hover:bg-red-100"
        >
          Reload the page
        </button>
      </div>
    </div>
  );
}

export { DefaultFallback, keysChanged };
export default ErrorBoundary;
